import React from 'react';
import { createFilterQuery, postFetchUserMessage} from '../../services/profile';
import useAuth from '../hooks/useAuth';
import ProfileFilters from '../profiles/ProfileFilters';
import { DataGrid } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import { createTheme } from '@mui/material/styles';
import { green, red } from '@mui/material/colors';
import {
	Box,
	FormControlLabel,
	IconButton,
	InputBase,
	Modal,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
	adminEnableOrDisableProfile,
	adminEnableOrDisableUser,
	adminFilter,
	adminProfileValidityExtend,
	adminTriggerReindex,
} from '../../services/admin';
import { toast, ToastContainer } from 'react-toastify';
import { Android12Switch } from '../common/Swtich';
import { cacheFilterState } from "../common/utilities/FilterTranslator";
import { FAILED_TO_FETCH_PROFILES_MESSAGE } from '../constants/Constants';

export const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 400,
	bgcolor: 'background.paper',
	border: '2px solid #000',
	boxShadow: 24,
	p: 4,
};

export const theme = createTheme({
	palette: {
		green: {
			main: green[500],
		},
		red: {
			main: red[500],
		},
	},
});

export const AdminHome = () => {
	const { getToken, filterState, setFilterState, areFiltersLoaded } = useAuth();
	const [localFilterState, setLocalFilterState] = React.useState(filterState)
	const [filteredProfiles, setFilteredProfiles] = React.useState([]);
	const [searchText, setSearchText] = React.useState(null);
	const pageSize = 10;
	const [currentPage, setCurrentPage] = React.useState(1);
	const [totalPages, setTotalPages] = React.useState(1);
	const [loading, setLoading] = React.useState(true);
	const [validityExtensionUser, setValidityExtensionUser] =
		React.useState(null);

	const columns = [
		{
			field: 'profile.firstName',
			headerName: 'Name',
			width: 150,
			renderCell: (entity) => {
				return (
					<Link
						to={`/administratorpath/viewprofile/${entity.row.profile.id}`}
						className='sign-up sign-up-button'
					>
						{entity.row.profile.firstName +
							' ' +
							entity.row.profile.lastName}
					</Link>
				);
			},
		},
		{
			field: 'profile.contactEmail',
			headerName: 'Contact Email',
			width: 150,
			renderCell: (entity) => {
				return entity.row.profile.contactEmail;
			},
		},
		{
			field: 'profile.contactNumber',
			headerName: 'Contact Number',
			width: 150,
			renderCell: (entity) => {
				return entity.row.profile.contactNumber;
			},
		},
		{
			field: 'isGotraAndKuldevtaAvail',
			headerName: 'Gotra & Kuldevta',
			width: 150,
			renderCell: (entity) => {
				const hasGotra = entity.row.profile.gotra && entity.row.profile.gotra.trim() !== '';
				const hasKuldevta = entity.row.profile.kuldevta && entity.row.profile.kuldevta.trim() !== '';
				return (
					<Typography color={hasGotra && hasKuldevta ? 'success.main' : 'error.main'}>
						{hasGotra && hasKuldevta ? 'Available' : 'Not Available'}
					</Typography>
				);
			},
		},
		{
			field: 'isImageAvailable',
			headerName: 'Images',
			width: 150,
			renderCell: (entity) => {
				return (
					<Typography color={entity.row.profile.isImageAvailable ? 'success.main' : 'error.main'}>
						{entity.row.profile.isImageAvailable ? 'Available' : 'Not Available'}
					</Typography>
				);
			},
		},
		{
			field: 'profile.disabled',
			headerName: 'Profile Enable/Disable',
			width: 200,
			renderCell: (entity) => {
				return (
					<Stack
						direction='row'
						spacing={1}
						alignItems='center'
						style={{ width: '100%', justifyContent: 'center' }}
					>
						<FormControlLabel
							control={
								<Android12Switch
									checked={!entity.row.profile.disabled}
									onChange={() => {
										enableDisableProfile(
											entity.row.id,
											entity.row.profile.disabled
										);
									}}
								/>
							}
						/>
					</Stack>
				);
			},
		},
		{
			field: 'user.disabled',
			headerName: 'User Enable/Disable',
			width: 200,
			renderCell: (entity) => {
				return (
					<Stack
						direction='row'
						spacing={1}
						alignItems='center'
						style={{ width: '100%', justifyContent: 'center' }}
					>
						<FormControlLabel
							control={
								<Android12Switch
									checked={!entity.row.user.disabled}
									onChange={() => {
										enableDisableUser(
											entity.row.id,
											entity.row.user.disabled
										);
									}}
								/>
							}
						/>
					</Stack>
				);
			},
		},
		{
			field: 'user.remainingValidityMillis',
			headerName: 'Validity',
			width: 200,
			renderCell: (entity) => {
				let currentDate = new Date().getTime();
				let remainingValidityInMillis = parseFloat(
					entity.row.user.remainingValidityMillis
				);
				let validityInMillis = currentDate + remainingValidityInMillis;
				let validityDate = new Date(validityInMillis);
				return <>{validityDate.toDateString()}</>;
			},
		},
		{
			field: 'extendValidity',
			headerName: 'Extend Validity',
			width: 200,
			renderCell: (entity) => {
				let remainingValidityInMillis = parseFloat(
					entity.row.user.remainingValidityMillis
				);
				return (
					<>
						{remainingValidityInMillis < 0 && (
							<Button
								fullWidth
								variant='contained'
								sx={{ m: 2 }}
								onClick={() =>
									setValidityExtensionUser({
										userName:
											entity.row.profile.firstName +
											' ' +
											entity.row.profile.lastName,
										userId: entity.row.id,
									})
								}
							>
								Extend Validity
							</Button>
						)}
					</>
				);
			},
		},
	];	  
	
	// firstRender indicates wether we are loading the profiles first time. We need this to keep filterState and localfilterstate in sync
	const applyProfileFilter = (firstRender = false, currentPagePassed = null) => {
		setLoading(true);
		if(currentPagePassed === null || currentPagePassed === undefined || currentPage === 0)
		  currentPagePassed = 1;
	
		var applicableFilters = firstRender ? filterState :localFilterState;
    	let filters = [],
	      age = applicableFilters.ageFilterState, height = applicableFilters.heightFilterState;
		Object.keys(applicableFilters).forEach((key) => {
			if (key !== 'ageFilterState' && key !== 'heightFilterState') {
				filters.push(applicableFilters[key]);
			}
		});
		let listOfFilters = createFilterQuery(filters, age, '', height);
		let auth = getToken();
		if (auth) {
			try {
				adminFilter(
					auth.token,
					listOfFilters,
					currentPagePassed,
					pageSize,
					searchText
				).then((response) => {
					setLoading(false);
					if (response.data.errors) {
						// setAllProfiles([]);
						alert(
							'Issue in fetching profiles please try refreshing'
						);
						throw new Error(response.data.errors);
					} else {
						let entities, totalPages;
						if (response.data.data.adminFilter) {
							entities = response.data.data.adminFilter.users;
							totalPages =
								response.data.data.adminFilter.total_pages;
						} else {
							entities = response.data.data.adminSearch.users;
							totalPages =
								response.data.data.adminSearch.total_pages;
						}
						postFetchUserMessage(totalPages);

						setFilteredProfiles(entities);
						setTotalPages(totalPages);
						cacheFilterState({...applicableFilters});
						setFilterState({...applicableFilters});
						firstRender && setLocalFilterState({...applicableFilters});
						setCurrentPage(currentPagePassed);
					}
				});
			} catch (error) {
				console.error("error response from backend in fetching profiles");
				toast.error(FAILED_TO_FETCH_PROFILES_MESSAGE, {timeOut: 4000});
				setLoading(false);
				setFilteredProfiles([]);
				setCurrentPage(1);
			}
		}
	};

	const enableDisableUser = (userId, enableUser) => {
		let auth = getToken();
		if (auth) {
			try {
				adminEnableOrDisableUser(auth.token, userId, enableUser).then(
					(response) => {
						if (response.data.errors) {
							// setAllProfiles([]);
							throw new Error(response.data.errors);
						} else {
							toast.info(
								enableUser ? 'User Enabled' : 'User Disabled',
								{ timeOut: 1000 }
							);
							// applyProfileFilter();
							let currentEntities = [...filteredProfiles];
							currentEntities.forEach((entity) => {
								if (entity.id === userId) {
									entity.user.disabled = !enableUser;
								}
							});
							setFilteredProfiles([...currentEntities]);
						}
					}
				);
			} catch (error) {
				console.error('Error in enabling or disabling profile', error);
				alert('There was an error when updating profile enabled state');
			}
		}
	};

	const enableDisableProfile = (userId, enableProfile) => {
		let auth = getToken();
		if (auth) {
			try {
				adminEnableOrDisableProfile(
					auth.token,
					userId,
					enableProfile
				).then((response) => {
					if (response.data.errors) {
						// setAllProfiles([]);
						throw new Error(response.data.errors);
					} else {
						toast.info(
							enableProfile
								? 'Profile Enabled'
								: 'Profile Disabled',
							{ timeOut: 1000 }
						);
						// applyProfileFilter();
						let currentEntities = [...filteredProfiles];
						currentEntities.forEach((entity) => {
							if (entity.id === userId) {
								entity.profile.disabled = !enableProfile;
							}
						});
						setFilteredProfiles([...currentEntities]);
					}
				});
			} catch (error) {
				console.error('Error in enabling or disabling profile', error);
				alert('There was an error when updating profile enabled state');
			}
		}
	};

	const extendValidity = () => {
		let auth = getToken();
		if (auth && validityExtensionUser) {
			try {
				adminProfileValidityExtend(
					auth.token,
					validityExtensionUser.userId
				).then((response) => {
					if (response.data.errors) {
						// setAllProfiles([]);
						throw new Error(response.data.errors);
					} else {
						toast.info('Profile Validity extended by one year', {
							timeOut: 1000,
						});
						setValidityExtensionUser(null);
						applyProfileFilter();
						// let currentEntities = [...filteredProfiles];
						// currentEntities.forEach((entity) => {
						// 	if (entity.id === userId) {
						// 		entity.profile.disabled =
						// 			!entity.profile.disabled;
						// 	}
						// });
						// setFilteredProfiles([...currentEntities]);
					}
				});
			} catch (error) {
				console.error('Error in profile validity extension', error);
				alert(
					'Issue in extending profiles validity please try refreshing'
				);
			}
		}
	};

	const triggerReindex = () => {
		let auth = getToken();
		if (auth) {
			try {
				adminTriggerReindex(auth.token).then((response) => {
					if (response.data.errors) {
						throw new Error(response.data.errors);
					} else if (!response.data.data.adminTriggerCompleteSearchIndexRebuild) {
						throw new Error('Trigger search index rebuild responded with failure');
					} else {
						toast.success('Search index rebuild triggered successfully', {
							timeOut: 2000,
						});
					}
				});
			} catch (error) {
				console.error('Error in triggering reindex', error);
				toast.error('Failed to trigger search index rebuild', {
					timeOut: 2000,
				});
			}
		}
	};
	//apply filter only if someone logs in
	React.useEffect(() => {
	if(areFiltersLoaded)
		applyProfileFilter(areFiltersLoaded);
	}, [areFiltersLoaded])

	const resetSearchText = () => {
		setSearchText("");
	}

	return (
		<>
			<ToastContainer />
			<Modal
				open={validityExtensionUser !== null}
				onClose={() => setValidityExtensionUser(null)}
			>
				<Box sx={modalStyle}>
					<Typography id='modal-modal-description' sx={{ mt: 2 }}>
						Are you sure you want to extend the payment validity of
						user <b>{validityExtensionUser?.userName}</b> by one
						year?
					</Typography>
					<Button
						fullWidth
						variant='contained'
						sx={{ mt: 2 }}
						onClick={extendValidity}
					>
						Extend Validity
					</Button>
				</Box>
			</Modal>
			<div className='container-fluid mt-1 mb-1'>
				<div className='row home-row'>
					<div className='p-1 col-xs-12 col-sm-12 col-md-4 col-lg-3 relative'>
						<ProfileFilters
							applyProfileFilter={applyProfileFilter}
							filterState={localFilterState}
							setFilterState={setLocalFilterState}
							appliedFilters={filterState}
							resetSearchText={resetSearchText}
						/>
					</div>
					<div
						className='col p-1 home-profiles'
						style={{ height: '600px', overflow: 'scroll' }}
					>
						<div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Button
								variant="contained"
								color="primary"
								onClick={triggerReindex}
								style={{ marginRight: '10px' }}
							>
								Rebuild Search Index
							</Button>
						</div>
						<div
							style={{
								width: '100%',
								marginTop: 5,
								marginBottom: 5,
							}}
						>
							<Paper
								component='form'
								sx={{
									p: '2px 4px',
									m: '0 auto',
									display: 'flex',
									alignItems: 'center',
								}}
								onSubmit={(event) => {
									event.preventDefault();
									applyProfileFilter();
								}}
							>
								<InputBase
									sx={{ ml: 1, flex: 1 }}
									placeholder='Start looking for a partner'
									textAlign='center'
									inputProps={{ 'aria-label': 'search ' }}
									value={searchText}
									onChange={(event) => {
										event.preventDefault();
										setSearchText(event.target.value);
									}}
								/>
								<IconButton
									type='button'
									sx={{ p: '10px' }}
									aria-label='search'
									onClick={() => applyProfileFilter()}
								>
									<SearchIcon
										style={{
											textAlign: 'center',
										}}
									/>
								</IconButton>
							</Paper>
						</div>
						{loading ? (
							<div className="flex justify-center">
								<span className="loading loading-dots loading-lg"></span>
							</div>
							) : (
							<DataGrid
								rows={filteredProfiles}
								columns={columns}
								pageSize={pageSize}
								page={currentPage - 1}
								paginationMode='server'
								onPageChange={(newpage) =>
									applyProfileFilter(false, newpage+1)
								}
								rowCount={totalPages * pageSize}
							/>
						)}
						
					</div>
				</div>
			</div>
		</>
	);
};
